import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import {useSnackbar} from 'notistack';
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {Link as RouterLink} from 'react-router-dom';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {getMe} from '../../../graphql/auth';
import {PaginatiorInfo} from '../../../graphql/common.datatypes';
import {Report} from '../../../graphql/reports.datatypes';
import deleteReport from './deleteReport.api';
import cloneReport from './cloneReport.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'relative',
    },
    content: {
        padding: 0
    },
    inner: {
      [theme.breakpoints.up('md')]: {
        minWidth: 700,
      },
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center'
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: 'flex-end'
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        '&:hover': {
            borderColor: theme.palette.error.dark
        }
    },
}));

const Results = (props: {
    title: string,
    className?: string,
    data: Report[],
    paginatorInfo?: PaginatiorInfo,
    setPage?: (page: number) => void,
    rowsPerPage?: number,
    setRowsPerPage?: (rowsPerPage: number) => void,
}) => {
    const {title, className, data, paginatorInfo, setPage, rowsPerPage, setRowsPerPage, ...rest} = props;
    const classes = useStyles();
    const userData = getMe();
    const myEmail = userData.data ? userData.data.me.email : '';
    const [open, setOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(-1);
    const [sendDeleteReport] = deleteReport();
    const [sendCloneReport] = cloneReport();
    const {enqueueSnackbar} = useSnackbar();

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        if(setPage) setPage(page + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if(setRowsPerPage) setRowsPerPage(Number(event.target.value));
    };

    const handleClickOpen = (reportId: number) => {
        setOpen(true);
        setSelectedId(reportId);
    };

    const handleDelete = () => {
        sendDeleteReport({variables: {id: selectedId}})
            .then(() => {
                enqueueSnackbar('Report Deleted.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Deleted Report Failed!', {variant: 'error'});
            });

        handleClose();
    };

    const handleClickClone = (reportId: number) => {
        sendCloneReport({variables: {id: reportId}})
            .then(() => {
                enqueueSnackbar('Report Cloned.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Clone Report Failed!', {variant: 'error'});
            });
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div
            {...rest}
            className={clsx(classes.root, className)}
        >
          <Card>
              <CardHeader
                title={title}
              />
              <Divider/>
              <CardContent className={classes.content}>
                  <PerfectScrollbar>
                      <div className={classes.inner}>
                          <Table>
                              <TableHead>
                                  <TableRow>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Owner</TableCell>
                                      <TableCell align="right">Actions</TableCell>
                                  </TableRow>
                              </TableHead>
                              <TableBody>
                                  {data.map(report => (
                                    <TableRow hover key={report.id}>
                                        <TableCell>{report.name}</TableCell>
                                        <TableCell>{(report.user!.email === myEmail) ? 'me' : report.user!.email}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                color="primary"
                                                component={RouterLink}
                                                size="small"
                                                to={`/reports/${report.url_key}`}
                                                variant="outlined"
                                            >
                                                View
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleClickClone(report.id)}
                                            >
                                                Clone
                                            </Button>
                                            {report.user!.email === myEmail && (
                                                <Button
                                                    className={classes.deleteButton}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleClickOpen(report.id)}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                          </Table>
                      </div>
                  </PerfectScrollbar>
              </CardContent>
              {paginatorInfo && rowsPerPage && (<CardActions className={classes.actions}>
                  <TablePagination
                    component="div"
                    count={paginatorInfo.total}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    page={paginatorInfo.currentPage - 1}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                  />
              </CardActions>)}
          </Card>

        <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
      </div>
    );
};

export default Results;
